import routes from 'js/core_pre_webpack/routes';
import { keptAlivePost } from 'js/utils/kept_alive_post';

/**
 * Record that this visitor was exposed to the Chrome-extension fake-door wave.
 *
 * ── Why not `abTest()` ─────────────────────────────────────────────────────
 * The generated module's `abTest()` DERIVES the variant client-side and records
 * it. Deriving is exactly what must not happen here: the browser's
 * `abTestVariant` has no rollout branch, so it would hand out arms regardless of
 * the staged rollout — the parity problem `AbTestFrontendRolloutChecker` exists
 * to catch. The server already decided (and gated on rollout) and shipped the
 * answer in `BulkDownloadFlags.extensionVariant`; this only reports it back so
 * the bucketing lands in `user_tests`.
 *
 * `POST /v0/ab_tests` re-resolves through `new_ab_test(test,
 * frontend_variant:)`, so the rollout gate applies a second time on the way in:
 * a variant reported for a visitor outside the rollout cannot be recorded as
 * anything but control.
 *
 * ── Why the exposure is recorded here and not at render ────────────────────
 * The server resolves read-only on every paper page. Recording there would
 * enrol everyone who merely LOADED a paper, most of whom never click Download,
 * and the per-arm rates would then be diluted by page traffic instead of
 * measuring the pitch. The download click is the exposure, so it is the
 * bucketing.
 *
 * Deduplicated through `sessionStorage` under the same key shape `__abTest_base`
 * uses, because two callers resolve the arm in one flow —
 * `show_bulk_download_modal` for the download timing and `designOverride` when
 * the modal renders — and one visit is one bucketing.
 */
export function recordExtensionBucketing(test: string, variant: string): void {
  if (typeof window === 'undefined') return;

  const key = `ab_test:${test}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, variant);
  } catch {
    // Private-mode storage can throw. Recording twice is a worse outcome than
    // not deduplicating, so bail rather than fall through.
    return;
  }

  keptAlivePost(routes.api_v0_ab_tests_path({ subdomain_param: 'api' }), {
    test,
    variant,
  });
}
